import React, { SyntheticEvent, useEffect, useState } from 'react';
import { Redirect } from 'react-router';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import * as Sentry from '@sentry/browser';
import styled from 'styled-components';

import Avatar from '@common/Avatar';
import PrimaryButton from '@common/button';
import ErrorMessage from '@common/fields/_common/errorMessage';
import TextField from '@common/fields/TextField';
import ModalComponent from '@common/modal';
import { Heading4, Heading5, Text } from '@common/Typography';
import {
  mapAddressDetailsToSkedifyAddress,
  mapAddressDetailsToString,
} from '@common/utils/hooks/google/utils';
import useQueryParams from '@common/utils/hooks/useQueryParams';
import { useScopedTranslation } from '@common/utils/hooks/useScopedTranslation';
import mapApolloError, {
  isDuplicateAppointmentError,
} from '@common/utils/mapApolloError';
import {
  CallbackQuery,
  ListingsQuery,
  MeetingTypeLabel,
  SubjectsQuery,
} from '@typings/api';
import { DateStyle, formatTime, formatToLocaleDateString } from '@utils/date';

import { FormValues } from '../BookingFlow';
import { MeetingTypeIconMap } from '../MeetingTypes';
import { CREATE_APPOINTMENT } from '../mutations';
import { CALLBACK, LISTINGS, SUBJECTS } from '../queries';

import {
  AppointmentInfo,
  AppointmentInfoContainer,
  AppointmentInfoContent,
  AppointmentInfoDayOfMonth,
  AppointmentInfoMeetingTypeIcon,
  AppointmentInfoMeta,
  AppointmentInfoMonth,
  MeetingTypeInfoText,
  MetaText,
} from './AppointmentInfo';

const FormHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 2.75rem;
  margin-top: 1rem;
`;

const AppointmentError = styled(ErrorMessage)`
  font-size: 0.8125rem;
  height: auto;
  margin-left: 1rem;
`;

const ButtonWrapper = styled.div`
  align-items: center;
  display: flex;
  margin-top: 1rem;

  > button {
    margin-top: 0;
  }
`;

const FooterText = styled.p`
  color: ${({ theme: { colors } }) => colors.GREY_3};
  font-size: 0.675rem;
  padding: 1rem 0 0 0;
`;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  formValues: FormValues;
}

export default function ConfirmModal({ formValues, isOpen, onClose }: Props) {
  const {
    address,
    addressKey,
    answers,
    contact,
    listingId,
    meetingType,
    selectedOffice,
    subjectId,
    timeSlot,
  } = formValues;

  const { i18n, t } = useScopedTranslation();
  const { id: contextId } = useQueryParams();

  const client = useApolloClient();

  const [mutationError, setMutationError] = useState<string | null>();
  const [notes, setNotes] = useState<string | undefined>('');

  const footerText = t('main_form.confirm_modal.footer_text', {
    defaultValue: '',
  });

  const meetingTypeDescription =
    meetingType &&
    t(
      `main_form.meeting_type.types.${meetingType.toLowerCase()}.short_description`,
    );

  const { data: listingsData } = useQuery<ListingsQuery>(LISTINGS, {
    variables: {
      contextId,
    },
    skip: !contextId || !listingId,
  });
  const listingUuid = (listingsData?.listings ?? []).find(
    (listing) => listing.id === listingId,
  )?.uuid;

  const { data: subjectsData } = useQuery<SubjectsQuery>(SUBJECTS, {
    variables: { contextId },
    skip: !contextId,
  });
  const subjects = subjectsData?.subjects ?? [];
  const subject = subjects.find((subject) => subject.id === subjectId);

  const { data: callbackData, loading: callbackLoading } =
    useQuery<CallbackQuery>(CALLBACK, {
      variables: { contextId },
      skip: !contextId,
    });
  const callback = callbackData?.context?.callback?.enabled ?? false;

  const [createAppointment, { data, error, loading }] = useMutation(
    CREATE_APPOINTMENT,
    {
      onError: (error) => {
        Sentry.withScope((scope) => {
          scope.setExtra('values', formValues);
          Sentry.captureException(error);
        });
      },
    },
  );
  const appointmentId = data?.createAppointment?.appointmentId;
  const hasDuplicateError =
    mutationError !== null &&
    error !== undefined &&
    isDuplicateAppointmentError(error);

  async function handleCreateAppointment(
    event: SyntheticEvent<HTMLButtonElement>,
  ) {
    event.preventDefault();
    event.stopPropagation();

    createAppointment({
      variables: {
        input: {
          forced: !callback,
          answers,
          contactId: contact ? contact.id : undefined,
          contextId,
          listingUuid,
          meetingType,
          officeId: selectedOffice?.id,
          subjectId,
          notes,
          timeSlots: [
            {
              contacts: timeSlot.contacts,
              end: timeSlot.endDate,
              start: timeSlot.startDate,
            },
          ],
          usedAddress:
            meetingType === MeetingTypeLabel.OFFICE
              ? undefined
              : mapAddressDetailsToSkedifyAddress(address ?? {}),
          usedAddressKey: addressKey,
        },
      },
    });
  }

  function handleClose() {
    onClose();
    setMutationError(null);
  }

  async function handleReload() {
    await client.clearStore();
    await client.reFetchObservableQueries();

    handleClose();
  }

  useEffect(() => {
    if (error !== undefined) {
      setMutationError(mapApolloError(error));
    }
  }, [error]);

  if (!isOpen) {
    return null;
  }

  return (
    <ModalComponent
      onClose={handleClose}
      title={t('main_form.confirm_modal.title')}
    >
      {appointmentId && (
        <Redirect
          to={`/confirm?appointmentId=${appointmentId}${
            contextId ? '&id=' + contextId : ''
          }`}
        />
      )}
      {subjects?.length !== 1 && (
        <Heading4>{subject ? subject.title : ''}</Heading4>
      )}
      <AppointmentInfoContainer disabled={hasDuplicateError}>
        <AppointmentInfo>
          <AppointmentInfoMeta>
            {timeSlot.startDate && (
              <>
                <AppointmentInfoDayOfMonth>{`${formatToLocaleDateString(
                  timeSlot.startDate,
                  i18n.language,
                  {
                    day: DateStyle.NUMERIC,
                    timezone: address?.timezone ?? undefined,
                  },
                )}`}</AppointmentInfoDayOfMonth>
                <AppointmentInfoMonth>{`${formatToLocaleDateString(
                  timeSlot.startDate,
                  i18n.language,
                  {
                    month: DateStyle.LONG,
                    timezone: address?.timezone ?? undefined,
                  },
                )}`}</AppointmentInfoMonth>
              </>
            )}
          </AppointmentInfoMeta>
          <AppointmentInfoContent>
            {timeSlot.startDate && timeSlot.endDate && (
              <Text>
                {`${formatTime(
                  timeSlot.startDate,
                  address?.timezone,
                )} - ${formatTime(timeSlot.endDate, address?.timezone)}`}
              </Text>
            )}
          </AppointmentInfoContent>
        </AppointmentInfo>
        <AppointmentInfo>
          <AppointmentInfoMeta>
            {meetingType && (
              <AppointmentInfoMeetingTypeIcon
                icon={MeetingTypeIconMap[meetingType]}
              />
            )}
          </AppointmentInfoMeta>
          <AppointmentInfoContent>
            {selectedOffice &&
              !selectedOffice.virtual &&
              meetingType === MeetingTypeLabel.OFFICE && (
                <>
                  <Text>{t('main_form.confirm_modal.partner_branch')}</Text>
                  <MetaText>{`${selectedOffice.title} - ${selectedOffice.location}`}</MetaText>
                </>
              )}
            {meetingTypeDescription && (
              <MeetingTypeInfoText>
                {meetingTypeDescription}
              </MeetingTypeInfoText>
            )}
            {meetingType === MeetingTypeLabel.ON_LOCATION && address && (
              <MetaText>{mapAddressDetailsToString(address)}</MetaText>
            )}
          </AppointmentInfoContent>
        </AppointmentInfo>
        {contact != null && (
          <AppointmentInfo>
            <AppointmentInfoMeta>
              <Avatar
                firstName={contact.firstName}
                fontSize={2}
                lastName={contact.lastName}
                size={4}
                src={contact.profilePicture}
              />
            </AppointmentInfoMeta>
            <AppointmentInfoContent>
              <Text>{contact.fullName}</Text>
            </AppointmentInfoContent>
          </AppointmentInfo>
        )}
      </AppointmentInfoContainer>

      {!hasDuplicateError && (
        <>
          <FormHeader>
            <Heading5>{t('main_form.confirm_modal.notes.title')}</Heading5>
          </FormHeader>
          <TextField onChange={setNotes} value={notes} />
        </>
      )}
      <ButtonWrapper>
        <PrimaryButton
          disabled={loading || callbackLoading}
          loading={loading}
          onClick={
            mutationError == null ? handleCreateAppointment : handleReload
          }
        >
          {t(
            `main_form.confirm_modal.${
              hasDuplicateError ? 'reload_button' : 'button'
            }`,
          )}
        </PrimaryButton>
        {mutationError && <AppointmentError>{mutationError}</AppointmentError>}
      </ButtonWrapper>
      {footerText && <FooterText>{footerText}</FooterText>}
    </ModalComponent>
  );
}
